import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {makeStyles} from 'tss-react/mui';
import {ApiButton, Box, ContentTemplate, TextFieldForm, todayDate, Typography} from 'web-library';
import {usePostRetrievePaymentDetailsForm} from './logic/usePostRetrievePaymentDetailsForm';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import {ID_TYPE_MENU_ITEMS} from '../../../accountManagement/logic/useCreateCandidateForm';
import SchoolCandidatePaymentDetails from './SchoolCandidatePaymentDetails';
import {RetrievePaymentDetailsFormData} from './schema/schemaRetrievePaymentDetails';
import {LookupListApiItem} from "../../../../api/user/model";

const useStyles = makeStyles()((theme) =>
    ({
      input: {
        marginBottom: theme.spacing(4),
      },
      dropdownTextField: {
        marginBottom: theme.spacing(3),
      },
      halfWidthLeftItem: {
        width: '50%',
        paddingRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingRight: 0,
        },
      },
      errorText: {
        color: theme.color.state.stateError,
        marginBottom: theme.spacing(2),
      },
      halfWidthRightItem: {
        width: '50%',
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingLeft: 0,
        },
      },
      responsiveFlexContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
    }));
const RetrievePaymentDetailsForm = () => {
  const {classes} = useStyles();
  const {t} = useTranslation();
  const date = todayDate;
  const examYearOptionsStart = date.getFullYear() - 3;
  const examYearOptions = [...Array(4).keys()].map((i) => i + examYearOptionsStart);
  const [organisationUuid, setOrganisationUuid] = useState<string>('');
  const {
    form,
    schools,
    retrievePaymentDetailsResponse,
    postRetrievePaymentDetailsMutation,
    postRetrievePaymentDetailsApiStatus,
    errorMessage,
  } = usePostRetrievePaymentDetailsForm();
  const {handleSubmit, control, formState: {errors}, register} = form;
  const handleRetrievePaymentDetailsFormSubmission = (formData: RetrievePaymentDetailsFormData) => {
    postRetrievePaymentDetailsMutation({
      idType: formData.IdType === 'P' ? 'PASSPORT' : 'NRIC_OR_FIN',
      uinFinPassport: formData.uinFinPassport,
      schoolUuid: formData.school.code,
      examYear: formData.examYear,
      email: formData.email,
    });
    setOrganisationUuid(formData.school.code);
  };

  return (
      <>
        <ContentTemplate continuous isDividerBottom>
          <form onSubmit={handleSubmit(handleRetrievePaymentDetailsFormSubmission)}>
            <Box className={[classes.input, classes.responsiveFlexContainer].join(' ')}>
              <ControlledAutocomplete<{ key: string, value: string, children: string }, false, false, undefined>
                  label={t('components->>SchoolCandidatePayment.IdType')}
                  className={classes.halfWidthLeftItem}
                  isRequired
                  control={control}
                  name="IdType"
                  id="inputSelect-idType"
                  error={!!errors.IdType}
                  helperText={errors?.IdType?.message?.toString()}
                  options={ID_TYPE_MENU_ITEMS}
                  getOptionLabel={({children}) => children}
                  placeholder={t('components->>SchoolCandidatePayment.IdTypePlaceHolder')}
                  transform={{
                    input: (selectedOption, options) =>
                        options.find((option) => option.value === selectedOption) ?? null,
                    output: (selectedOption) => (selectedOption ? selectedOption.value : null),
                  }}
              />
              <TextFieldForm
                  form={form}
                  className={classes.halfWidthRightItem}
                  label={t('components->>SchoolCandidatePayment.NRIC/FIN/Passport No.')}
                  isRequired
                  name="uinFinPassport"
                  type="text"
                  id="inputText-candidate-nric-fin-pass-no"
                  placeholder={t('components->>SchoolCandidatePayment.nricFinPassportPlaceHolder')}
                  size="medium"
                  error={!!errors.uinFinPassport}
                  helperText={errors?.uinFinPassport?.message?.toString()}
                  onChange={(e) => form.setValue('uinFinPassport', e.target.value.toUpperCase())}
                  fullWidth
              />
            </Box>
            <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
                label={t('components->>SchoolCandidatePayment.School')}
                className={classes.input}
                isRequired
                control={control}
                name="school"
                id="inputSelect-school"
                error={!!errors.school}
                helperText={errors?.school?.message?.toString()}
                options={schools ?? []}
                getOptionLabel={({description}) => description}
                placeholder={t('components->>SchoolCandidatePayment.SchoolPlaceHolder')}
            />
            <ControlledAutocomplete
                label={t('components->>SchoolCandidatePayment.ExamYear')}
                className={classes.input}
                isRequired
                control={control}
                name="examYear"
                id="inputSelect-examYear"
                error={!!errors.examYear}
                helperText={errors?.examYear?.message?.toString()}
                options={examYearOptions}
                getOptionLabel={(selected) => selected.toString()}
            />
            <TextFieldForm
                form={form}
                className={classes.input}
                label={t('components->>SchoolCandidatePayment.Email')}
                isRequired
                subLabel={t('components->>SchoolCandidatePayment.EmailAwareness')}
                name="email"
                type="text"
                id="inputText-email"
                placeholder={t('components->>SchoolCandidatePayment.EmailPlaceholder')}
                size="medium"
                error={!!errors.email}
                helperText={errors?.email?.message?.toString()}
                fullWidth
            />
            {errorMessage && (
                <Typography className={classes.errorText} variant="body2">
                  {errorMessage}
                </Typography>
            )}
            <ApiButton
                status={postRetrievePaymentDetailsApiStatus}
                successLabel="Retrieved"
                errorLabel="Unable to Retrieve"
                type="submit"
                className={classes.input}
                successTimeout={1000000}
                id="button-retrieve-candidate-payment"
            >
              Retrieve Payment Details
            </ApiButton>
          </form>
        </ContentTemplate>

        {retrievePaymentDetailsResponse && (
            <SchoolCandidatePaymentDetails
                schoolCandidatePaymentDetails={retrievePaymentDetailsResponse}
                organisationUuid={organisationUuid}
            />
        )}
      </>
  );
};

export default RetrievePaymentDetailsForm;
